// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import { Feature } from '@fulcrumapp/fulcrum-core';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import MetadataField from './metadata-field';
import LocationEntry from '../../data-view/map/location-entry';
import { calculatePointAlongPolyline } from '../../sketch-editor/geo-tools';

export default class LocationField extends MetadataField {
  static propTypes = {
    ...MetadataField.propTypes,
    onGeometryChange: PropTypes.func.isRequired,
    editing: PropTypes.bool.isRequired,
    isManualLocationEnabled: PropTypes.bool.isRequired,
    feature: PropTypes.instanceOf(Feature).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showLocationEntry: false,
    };
  }

  renderEditable() {
    const { styles } = LocationField;

    return (
      <div className={css(styles.container)}>
        {this.renderLocationLabel()}
        {this.renderLocationEditLink()}
        <LocationEntry
          show={this.state.showLocationEntry}
          feature={this.props.feature}
          onCancel={this.handleLocationEntryCancel}
          onDone={this.handleLocationEntryDone}
        />
      </div>
    );
  }

  renderLocationEditLink() {
    const { isManualLocationEnabled, feature: { latitude, longitude, geometry } } = this.props;
    const { styles } = LocationField;

    if (!isManualLocationEnabled) {
      return null;
    }

    const hasValidCoordinates = latitude != null && longitude != null;
    const isPolygonWithCoordinates = geometry && geometry.type !== 'Point' && hasValidCoordinates;

    if (!isPolygonWithCoordinates) {
      return (
        <a
          onClick={this.handleChangeClick}
          href="#"
          className={styles.change}
        >
          Change
        </a>
      );
    }

    return null;
  }

  renderLocationLabel() {
    const { styles } = LocationField;

    const label = this.locationLabel;
    const { geometry } = this.props.feature;

    if (!label?.length) {
      switch (geometry?.type) {
        case 'LineString':
        case 'MultiLineString': {
          const polyline = new Polyline(geojsonToArcGIS(geometry));
          const totalLength = geometryEngine.geodesicLength(polyline, 'kilometers');
          const midpoint = calculatePointAlongPolyline(polyline, totalLength / 2);

          const { latitude, longitude } = midpoint;

          return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }
        case 'MultiPolygon':
        case 'Polygon': {
          const polygon = new Polygon(geojsonToArcGIS(geometry));
          const { latitude, longitude } = polygon.centroid;

          return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }
        default:
          return <span className={styles.noLocation}>No Location</span>;
      }
    }

    return (
      <span className={styles.location}>
        {label}
        {this.renderAccuracy()}
      </span>
    );
  }

  renderAccuracy() {
    const { styles } = LocationField;

    if (this.props.feature.hasCoordinate && this.props.feature.horizontalAccuracy != null && this.props.feature.horizontalAccuracy >= 0) {
      return (
        <span className={css(styles.accuracy)}>
          (
          {parseInt(this.props.feature.horizontalAccuracy, 10)}
          m accuracy)
        </span>
      );
    }

    return null;
  }

  renderLabelContent() {
    const { geometry } = this.props.feature;

    return (
      <span>
        <strong className={MetadataField.styles.strong}>Location</strong>
        <span className={styles.center}>
          {' '}
          {this.renderCenterType(geometry?.type)}
        </span>
      </span>
    );
  }

  renderCenterType(type) {
    switch (type) {
      case 'LineString':
      case 'MultiLineString':
        return '(Approximate Midpoint)';
      case 'Polygon':
      case 'MultiPolygon':
        return '(Approximate Centroid)';
      default:
        return '';
    }
  }

  renderValueContent() {
    return this.renderLocationLabel();
  }

  handleChangeClick = (event) => {
    event.preventDefault();

    this.setState({ showLocationEntry: true });
  };

  handleLocationEntryCancel = () => {
    this.setState({ showLocationEntry: false });
  };

  handleLocationEntryDone = (coordinate) => {
    this.setState({ showLocationEntry: false });
    this.props.onGeometryChange(coordinate);
  };

  get locationLabel() {
    const { geometry, latitude, longitude } = this.props.feature;

    if (latitude != null && longitude != null) {
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }

    if (geometry?.type === 'Point') {
      const [long, lat] = geometry.coordinates;

      return `${lat.toFixed(6)}, ${long.toFixed(6)}`;
    }

    return <span className={styles.noLocation}>No Location</span>;
  }
}

const styles = cssInJS({
  container: {
    paddingTop: 10,
    paddingLeft: 4,
  },

  accuracy: {
    color: '#888',
    marginLeft: 4,
  },

  location: {
    marginRight: 8,
  },

  change: {
    color: '#008fdc',
    textDecoration: 'none',
  },

  center: {
    color: '#888',
    textDecoration: 'none',
  },

  noLocation: {
    color: '#888',
    fontStyle: 'italic',
    marginRight: 8,
  },
});

LocationField.styles = styles;
